<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState">
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <a-form-item label="Año" name="year" required>
        <YearPicker :value="formState.year" @update:value="formState.year = $event" />
      </a-form-item>
    </a-form>

    <div class="actions">
      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled || isReportLoading" :loading="isReportLoading" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <CustomScrollbar height="calc(100vh - 312px)">
    <a-spin :spinning="isReportLoading">
      <a-table
        class="table"
        rowClassName="table"
        size="middle"
        :columns="abandonedColumns"
        :data-source="reportData"
        :scroll="{ x: 1500 }"
        :pagination="false"
      />

      <PerMonthChart title="Total de abandonadas por mes" :data="reportData" />

      <a-divider class="divider" />

      <PerTimeSectionChart
        title="Total anual de abandonadas por tramo horario"
        :data="reportData"
      />

      <a-divider class="divider" />

      <LastTwoMonthComparativeChart
        title="Comparativo últimos dos meses por tramo horario"
        :data="reportData"
      />
    </a-spin>
  </CustomScrollbar>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";

import { fetchAbandonedType, downloadAbandonedType } from "@/app/reports/services";
import { abandonedColumns } from "@/app/reports/utils/tableColumns/customerSupport";
import { formatAbandonedCallsData } from "@/app/reports/utils/helpers";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import YearPicker from "@/app/shared/components/pickers/YearPicker.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import PerMonthChart from "./charts/abandoned/PerMonthChart.vue";
import PerTimeSectionChart from "./charts/abandoned/PerTimeSectionChart.vue";
import LastTwoMonthComparativeChart from "./charts/abandoned/LastTwoMonthComparativeChart.vue";

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);

// Form
const formState = reactive({
  portfolio: null,
  year: null,
});
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (!formState[key]) return true;
  }

  return false;
});
const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

// Table
const reportData = ref(null);
const isReportLoading = ref(false);

const fetchReportData = async () => {
  isReportLoading.value = true;

  const response = await fetchAbandonedType({ filters: formState });
  reportData.value = formatAbandonedCallsData({ rawData: response?.data });

  isReportLoading.value = false;
};

const handleSearch = async () => {
  await fetchReportData();
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadAbandonedType({ filters: formState });
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

:deep(.ant-form-item-label)
  padding: 0

:deep(.table.ant-table-row:last-child)
  background-color: $blue-1

:deep(.table.ant-table-row:last-child .ant-table-cell-fix-left)
  background-color: $blue-1 !important

.actions
  display: flex
  align-items: center
  gap: 10px

.divider
  background-color: $gray-4
</style>
